import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import { MemberLayout } from "./components/MemberLayout";
import { AdminLayout } from "./components/AdminLayout";
import { LoginPage } from "./pages/Login";
import { PasswordResetRequestPage } from "./pages/PasswordResetRequest";
import { PasswordResetConfirmPage } from "./pages/PasswordResetConfirm";
import { HomePage } from "./pages/Home";
import { EventsPage } from "./pages/Events";
import { EventDetailPage } from "./pages/EventDetail";
import { ContentsPage } from "./pages/Contents";
import { ContentDetailPage } from "./pages/ContentDetail";
import { NoticesPage } from "./pages/Notices";
import { PerksPage } from "./pages/Perks";
import { PerkDetailPage } from "./pages/PerkDetail";
import { NoticeDetailPage } from "./pages/NoticeDetail";
import { MyPage } from "./pages/MyPage";
import { QrPage } from "./pages/Qr";
import { TermsPage } from "./pages/Terms";
import { SurveyAnswerPage } from "./pages/SurveyAnswer";
import { FirstHearingPage } from "./pages/FirstHearing";
import { AdminHomePage } from "./pages/admin/AdminHome";
import { AdminMembersPage } from "./pages/admin/AdminMembers";
import { AdminMemberDetailPage } from "./pages/admin/AdminMemberDetail";
import { AdminEventsPage } from "./pages/admin/AdminEvents";
import { AdminSurveyPage } from "./pages/admin/AdminSurvey";
import { AdminCheckinPage } from "./pages/admin/AdminCheckin";
import { AdminNoticesPage } from "./pages/admin/AdminNotices";
import { AdminPerksPage } from "./pages/admin/AdminPerks";
import { AdminContentsPage } from "./pages/admin/AdminContents";
import { AdminHearingsPage } from "./pages/admin/AdminHearings";
import { AdminAuditPage } from "./pages/admin/AdminAudit";
import { AdminMyaspLogsPage } from "./pages/admin/AdminMyaspLogs";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, me } = useAuth();
  if (loading) return <FullSpinner />;
  if (!me?.user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { loading, me } = useAuth();
  if (loading) return <FullSpinner />;
  const isAdmin =
    me?.user.roles.includes("admin") ||
    me?.user.roles.includes("super_admin") ||
    me?.user.roles.includes("staff");
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function HearingGate({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  // 会員レコードがあり、ヒアリング未完了の場合は強制誘導
  if (me?.member && !me.member.hearingCompleted) {
    return <Navigate to="/first-hearing" replace />;
  }
  return <>{children}</>;
}

function FullSpinner() {
  return (
    <div className="flex h-screen items-center justify-center text-tora">
      Loading…
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/password-reset"
          element={<PasswordResetRequestPage />}
        />
        <Route
          path="/password-reset/confirm"
          element={<PasswordResetConfirmPage />}
        />

        <Route
          path="/first-hearing"
          element={
            <RequireAuth>
              <FirstHearingPage />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <HearingGate>
                <MemberLayout />
              </HearingGate>
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          <Route path="/events/:eventId/survey" element={<SurveyAnswerPage />} />
          <Route path="/contents" element={<ContentsPage />} />
          <Route path="/contents/:contentId" element={<ContentDetailPage />} />
          <Route path="/notices" element={<NoticesPage />} />
          <Route path="/notices/:noticeId" element={<NoticeDetailPage />} />
          <Route path="/perks" element={<PerksPage />} />
          <Route path="/perks/:perkId" element={<PerkDetailPage />} />
          <Route path="/mypage" element={<MyPage />} />
          <Route path="/mypage/qr" element={<QrPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Route>

        <Route
          path="/admin/*"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            </RequireAuth>
          }
        >
          <Route index element={<AdminHomePage />} />
          <Route path="members" element={<AdminMembersPage />} />
          <Route path="members/:memberId" element={<AdminMemberDetailPage />} />
          <Route path="events" element={<AdminEventsPage />} />
          <Route path="events/:eventId/survey" element={<AdminSurveyPage />} />
          <Route path="checkin" element={<AdminCheckinPage />} />
          <Route path="contents" element={<AdminContentsPage />} />
          <Route path="notices" element={<AdminNoticesPage />} />
          <Route path="perks" element={<AdminPerksPage />} />
          <Route path="hearings" element={<AdminHearingsPage />} />
          <Route path="audit-logs" element={<AdminAuditPage />} />
          <Route path="myasp-logs" element={<AdminMyaspLogsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
